<template>
    <header
        role="banner"
        :class="['header', displaySmallOrExtraSmall ? 'header-small' : '']"
    >
        <a
            class="skip-main"
            href="#main"
            @click="handleSkipLinkPress"
        >{{ $t('App.Header.SkipToContent') }}</a>
        <div :class="[ !displayLargeOrExtraLarge ? 'app-name-small' : 'app-name' ]">
            <a
                class="logo-link"
                :href="etusivuLink"
                :aria-label="$t('App.Logo.AriaLabel')"
                @keydown.space.prevent="navigateToLinkHref"
            >
                <span class="app-name-label" >{{ $t('App.Name') }}</span>
            </a>
        </div>
        <nav :aria-label="$t('App.MainNavigation.Heading')">
            <div
                v-if="!displaySmallOrExtraSmall"
                class="header-actions"
            >
                <a
                    :href="etusivuLink"
                    :class="[
                        'single-link',
                        !displaySmallOrExtraSmall && isSelected ? 'selected-nav-list' : '']"
                    @keydown.space.prevent="navigateToLinkHref"
                >
                    {{ etusivuHeader }}
                </a>
                <navigation-list
                    v-show="liikenneLinks.length > 0"
                    :header="liikenneHeader"
                    :links="liikenneLinks"
                />
                <navigation-list
                    v-show="viestintaLinks.length > 0"
                    :header="viestintaHeader"
                    :links="viestintaLinks"
                />

                <lang-change class="lang-change" />
            </div>
            <div
                v-else
                class="hamburger"
            >
                <button
                    v-if="!isBurgerMenuOpen"
                    class="burger-button"
                    :aria-expanded="isBurgerMenuOpen.toString()"
                    :aria-haspopup="true"
                    :aria-controls="'menu'"
                    :aria-label="burgerIconAlt"
                    @click="toggleHamburger"
                >
                    <img
                        v-if="!isBurgerMenuOpen"
                        class="burger-icon"
                        :src="burgerIcon"
                        aria-hidden="true"
                    >
                </button>
                <button
                    v-else
                    ref="closedBurgerButton"
                    class="close-burger-button"
                    :aria-expanded="isBurgerMenuOpen.toString()"
                    :aria-haspopup="true"
                    :aria-label="crossIconAlt"
                    aria-controls="burger-menu"
                    @click="toggleHamburger"
                    @keydown.esc="closeHamburgerMenu"
                >
                    <img
                        v-if="isBurgerMenuOpen"
                        class="burger-icon"
                        :src="crossIcon"
                        aria-hidden="true"
                    >
                </button>
                <div v-if="isBurgerMenuOpen">
                    <ul
                        id="burger-menu"
                        role="menu"
                        class="hamburger-container"
                    >
                        <!--kielivalikko-->
                        <lang-change
                            class="lang-change"
                            @close-hamburger-menu="closeHamburgerMenu"
                        />
                        <li role="none">
                            <a
                                role="menuitem"
                                :href="etusivuLink"
                                :class="[
                                    'single-link',
                                    displaySmallOrExtraSmall ? 'single-link-burger' : '' ,
                                    displaySmallOrExtraSmall && isSelected ? 'selected-nav-list-burger' : '']"
                                :aria-current="displaySmallOrExtraSmall && isSelected ? 'page' : false"
                                @keydown.esc="closeHamburgerMenu"
                                @keydown.space.prevent="navigateToLinkHref"
                            >
                                {{ etusivuHeader }}
                            </a>
                        </li>
                        <li role="none">
                            <navigation-list
                                :header="liikenneHeader"
                                :links="liikenneLinks"
                                @close-hamburger-menu="closeHamburgerMenu"
                            />
                        </li>
                        <li role="none">
                            <navigation-list
                                :header="viestintaHeader"
                                :links="viestintaLinks"
                                @close-hamburger-menu="closeHamburgerMenu"
                            />
                        </li>
                    </ul>
                </div>
            </div>
        </nav>
    </header>
</template>

<script lang="ts">
    import { Component, Vue, Watch } from "vue-facing-decorator"
    import LangChange from "@/components/LangChange.vue"
    import NavigationList from "@/layout/headerComponents/NavigationList.vue"
    import PageLinkUtils from "../helpers/pageLinkUtils"

    @Component({
        components: {
            "lang-change": LangChange,
            "navigation-list": NavigationList
        }
    })
    export default class Header extends Vue {
        pageLinkUtils = new PageLinkUtils()
        hamburgerOpened: boolean = false

        get liikenneLinks(): Array<any> {
            return this.pageLinkUtils.getLiikenneLinks()
        }

        get viestintaLinks(): Array<any> {
            return this.pageLinkUtils.getViestintaLinks()
        }

        get etusivuLink(): string | undefined {
            return this.pageLinkUtils.getEtusivuLink()
        }

        get liikenneHeader(): string | undefined {
            return this.$t("App.Header.Liikenne")
        }

        get viestintaHeader(): string | undefined {
            return this.$t("App.Header.Viestinta")
        }

        get etusivuHeader(): string | undefined {
            return this.$t("App.Header.Etusivu")
        }

        // ruutukoko
        get displayType(): string {
            return this.$store.getters.displayType
        }

        get displayLarge(): string {
            return this.$store.state.displayLarge
        }

        get displayExtraLarge(): string {
            return this.$store.state.displayExtraLarge
        }

        get displayMedium(): string {
            return this.$store.state.displayMedium
        }

        get displaySmall(): string {
            return this.$store.state.displaySmall
        }

        get displayExtraSmall(): string {
            return this.$store.state.displayExtraSmall
        }

        // ruutukoko on isompi kuin medium
        get displayLargeOrExtraLarge(): boolean {
            return this.displayType === this.displayExtraLarge || this.displayType === this.displayLarge
        }

        // ruutukoko on pienempi kuin medium
        get displaySmallOrExtraSmall(): boolean {
            return this.displayType === this.displaySmall || this.displayType === this.displayExtraSmall
        }

        get burgerIcon(): string {
            return this.$store.getters.contentServiceUrl + "/Content/icons/svg-blue/valikko.svg" + "?" + this.$store.getters.contentServiceTimeStamp
        }

        get crossIcon(): string {
            return this.$store.getters.contentServiceUrl + "/Content/icons/svg-blue/ruksi.svg" + "?" + this.$store.getters.contentServiceTimeStamp
        }

        get burgerIconAlt(): string | undefined {
            return this.$t("App.Header.BurgerIconAlt")
        }

        get crossIconAlt(): string | undefined {
            return this.$t("App.Header.CrossIconAlt")
        }

        get isSelected(): boolean {
            return this.$route.fullPath === "/"
        }

        get isBurgerMenuOpen(): boolean {
            return this.hamburgerOpened
        }

        @Watch("$route.params")
        onRouterParamChange() {
            // Piiloitetaan valikko
            this.hamburgerOpened = false
        }

        @Watch("displaySmallOrExtraSmall")
        onDisplaySmallOrExtraSmallChange(): void {
            if (!this.displaySmallOrExtraSmall) {
                this.hamburgerOpened = false
            }
        }

        toggleHamburger(): void {
            this.hamburgerOpened = !this.hamburgerOpened
        }

        closeHamburgerMenu(): void {
            (this.$refs.closedBurgerButton as HTMLButtonElement).focus()
            this.hamburgerOpened = false
        }

        navigateToLinkHref(event): void {
            window.location.href = event.target.href
        }

        handleSkipLinkPress() {
            this.$store.dispatch("setSkipToContentClicked", true)
        }
    }
</script>

<style scoped>
    .header {
        height: 5rem;
        display: flex;
        border-bottom: 20px solid #F2F4F8;
    }

    .header-small {
        border-bottom: 13px solid #F2F4F8;
    }

    .logo-link {
        background-image: none;
        position: relative;
        left: 20px;
    }
        .logo-link:focus {
            outline: 3px solid var(--Traficom-main-color);
            outline-offset: 2px;
        }

    .app-name {
        width: var(--var-slot-width-xl-l);
        align-self: center;
    }

    .app-name-small {
        width: var(--var-slot-width-xl-l);
        align-self: center;
        padding-right: 25px;
    }

    nav {
        flex-grow: 1;
        display: flex;
    }

    .header-actions {
        flex-grow: 1;
        display: flex;
        align-items: center;
        background-color: var(--Traficom-main-color);
    }

    .lang-change {
        width: 100%;
        z-index: 21;
    }

    .single-link {
        height: 100%;
        padding: 0px 20px;
        display: flex;
        align-items: center;
        color: #FFFFFF;
        font: normal normal 600 16px Montserrat;
        text-transform: uppercase;
        letter-spacing: 0.48px;
        cursor: pointer;
        background: none;
        border-bottom: 5px solid transparent;
        border-top: 5px solid transparent;
    }

        .single-link:hover {
            text-decoration: underline;
        }

        .single-link a:hover {
            background: none;
        }

        .single-link.selected-nav-list {
            border-bottom: 5px solid #FFFFFF;
        }

    .routerlink.single-link {
        min-width: fit-content;
    }

    /* hampurilaisen tyylit */
    .hamburger {
        flex-grow: 1;
        display: flex;
        align-items: center;
        background-color: #FFFFFF;
        justify-content: flex-end;
        margin-right: 20px;
    }
        .hamburger .single-link-burger:focus {
            outline: 3px solid white;
            outline-offset: -4px;
        }

    .hamburger-container {
        width: 100%;
        position: absolute;
        top: 60px;
        background-color: var(--Traficom-main-color);
        height: auto;
        z-index: 1020;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 10px 10px;
        left: 0;
    }

    .burger-button,
    .close-burger-button {
        background: none;
        border: none;
        padding: 0;
    }

        .burger-button:focus,
        .close-burger-button:focus {
            outline: 3px solid var(--Traficom-main-color);
            outline-offset: 2px;
        }

    .single-link.selected-nav-list-burger {
        border-left: 5px solid #FFFFFF;
    }

    .burger-icon {
        width: 1.375rem;
        cursor: pointer;
    }

    .single-link-burger {
        margin: 5px 0px;
        padding: 15px 20px 15px 15px;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-bottom: unset;
        border-top: unset;
        width: 100%;
    }

    /* end: hampurilaisen tyylit */

    a.skip-main {
        left: -999px;
        position: absolute;
        top: auto;
        width: 1px;
        height: 1px;
        overflow: hidden;
        z-index: -999;
    }

        a.skip-main:focus, a.skip-main:active {
            left: 50%;
            top: 60px;
            width: auto;
            height: auto;
            overflow: auto;
            margin: 10px;
            padding: 13px;
            text-align: center;
            font-size: 1.2em;
            z-index: 1002;
            background-color: var(--Traficom-main-color);
            color: white;
            font-weight: 400;
            background-image: none;
            background-size: unset;
            background-position: unset;
            background-repeat: unset;
            text-decoration: unset;
            cursor: pointer;
            outline: 3px solid var(--Traficom-main-color);
            outline-offset: 2px;
        }

        a.skip-main:hover {
            text-decoration: underline;
        }

    .header-actions .single-link:focus {
        outline: 3px solid white;
        outline-offset: -4px;
    }

</style>
